import React from "react";
import { Box, Stack } from "@mui/material";
import { isValid } from "date-fns";
import { DotLegalCheckbox, DotLegalDatePicker, DotLegalHeader, DotLegalTimePicker } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { checkboxBelowFieldMargin } from "./IncidentLogFields.styles.ts";

export interface IncidentLogDateTimeFieldProps {
    label: string;
    toolTipText?: string;
    date?: Date | null;
    time?: Date | null;
    onDateChange: (date: Date | null) => void;
    onTimeChange: (time: Date | null) => void;
    noDateCheckbox?: {
        label: string;
        checked: boolean;
        onChange: (checked: boolean) => void;
    };
    disabled: boolean;
    errorText?: string;
    children?: React.ReactNode;
}

function IncidentLogDateTimeField(props: IncidentLogDateTimeFieldProps) {
    const { getDateTimePickerLanguage } = useTranslation();
    const language = getDateTimePickerLanguage();
    const isDateTimeDisabled = props.disabled || (props.noDateCheckbox?.checked ?? false);

    // A time typed digit by digit is invalid until the minutes are in. Passing it on would save it as no time, and the
    // saved value coming back would reset the field and wipe the digits typed so far.
    function onTimeChange(time: Date | null) {
        if (time === null || isValid(time)) {
            props.onTimeChange(time);
        }
    }

    return (
        // A small bottom margin on top of the next field's own top margin, since several date fields in a row read as
        // one block with the top margin alone.
        <Box sx={{ mt: 2, mb: 2 }}>
            <DotLegalHeader marginBottom={1} headerStyle={"small"} toolTip={props.toolTipText}>
                {props.label}
            </DotLegalHeader>
            <Stack direction={"row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                    <DotLegalDatePicker
                        label=""
                        noMargin
                        value={props.date ?? null}
                        onChange={props.onDateChange}
                        disabled={isDateTimeDisabled}
                        language={language}
                        error={!isNullOrWhitespace(props.errorText)}
                        helperMessage={props.errorText}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <DotLegalTimePicker value={props.time ?? null} onChange={onTimeChange} disabled={isDateTimeDisabled} language={language} />
                </Box>
            </Stack>
            {props.noDateCheckbox && (
                <DotLegalCheckbox
                    label={props.noDateCheckbox.label}
                    checked={props.noDateCheckbox.checked}
                    onChange={props.noDateCheckbox.onChange}
                    disabled={props.disabled}
                    margin={checkboxBelowFieldMargin}
                />
            )}
            {props.children}
        </Box>
    );
}

export default IncidentLogDateTimeField;
